import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Input,
  Text,
  VStack,
  Alert,
  AlertIcon,
  AlertDescription,
  useToast,
  FormControl,
  FormLabel,
  Checkbox,
} from '@chakra-ui/react';
import {
  mundialTeamService,
  parseTeamsFromExcelBuffer,
} from '../../services/mundial/mundialTeamService';

interface ExcelTeamUploadProps {
  onUploadComplete: () => void;
}

export const ExcelTeamUpload: React.FC<ExcelTeamUploadProps> = ({
  onUploadComplete,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [replaceExisting, setReplaceExisting] = useState(true);
  const toast = useToast();

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setErrors([]);

    try {
      const buffer = await file.arrayBuffer();
      const { teams, errors: parseErrors } = parseTeamsFromExcelBuffer(buffer);

      if (parseErrors.length > 0) {
        setErrors(parseErrors);
        return;
      }

      if (teams.length === 0) {
        setErrors(['El archivo no contiene selecciones válidas']);
        return;
      }

      if (replaceExisting) {
        await mundialTeamService.replaceAllTeams(teams);
      } else {
        await mundialTeamService.saveTeams(teams);
      }

      toast({
        title: 'Selecciones cargadas',
        description: `Se guardaron ${teams.length} selecciones`,
        status: 'success',
        duration: 4000,
      });
      onUploadComplete();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al cargar el archivo';
      setErrors(message.split('\n'));
      toast({
        title: 'Error',
        description: message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <VStack align="stretch" spacing={4}>
      <Text fontSize="sm" color="gray.600">
        El Excel debe incluir columnas: Nombre, Código (o Abreviatura), Grupo (A–L),
        URL o Bandera (enlace de la bandera), Confederación (opcional). El orden de las
        filas en el archivo se respeta en las tablas de grupos (también puedes usar una
        columna Orden opcional).
      </Text>

      <FormControl>
        <Checkbox
          isChecked={replaceExisting}
          onChange={(e) => setReplaceExisting(e.target.checked)}
        >
          Reemplazar selecciones existentes
        </Checkbox>
      </FormControl>

      <FormControl>
        <FormLabel>Archivo Excel (.xlsx, .xls)</FormLabel>
        <Input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFile}
          isDisabled={loading}
        />
      </FormControl>

      <Button
        onClick={() => inputRef.current?.click()}
        isLoading={loading}
        colorScheme="blue"
        size="sm"
        variant="outline"
      >
        Seleccionar archivo
      </Button>

      {errors.length > 0 && (
        <Alert status="error">
          <AlertIcon />
          <Box>
            {errors.map((err) => (
              <AlertDescription key={err}>{err}</AlertDescription>
            ))}
          </Box>
        </Alert>
      )}
    </VStack>
  );
};
